import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import './SLAProfiles.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const BASE = `${API_BASE}/sla-profiles`;

/**
 * Settings -> SLA working hours (migration 114): which hours an SLA counts.
 *
 * Gordon, 30 October: SLAs are measured in WORKING hours. A PROFILE says
 * what those are - a working week in Malta time, Malta's public holidays in
 * or out, extra closed days, and (optionally) its own per-priority targets.
 * "Office hours" (Mon-Fri 08:00-17:00, holidays off) is the default and
 * "24/7" is there for anything that is promised round the clock. RULES pick
 * a profile for a category, a client company (a ticket's company is its
 * device's), or both; the most specific rule wins, otherwise the default.
 *
 * Every ticket keeps the profile it was raised with (a snapshot), so an edit
 * here never quietly moves a deadline already promised. The list says how
 * many open tickets are still on an earlier version of each profile, and
 * "Re-apply to open tickets" moves them on purpose.
 *
 * HOW MANY hours is still Settings -> SLA rules (per category and priority);
 * a profile's own targets, where it sets them, take precedence - that is
 * what "Alpha Ltd's contract: Critical in 4 hours" means.
 *
 * Every check the form makes the server makes as well (routes/sla-profiles.js).
 */

const DAYS = [
  ['mon', 'Monday'], ['tue', 'Tuesday'], ['wed', 'Wednesday'], ['thu', 'Thursday'],
  ['fri', 'Friday'], ['sat', 'Saturday'], ['sun', 'Sunday']
];
const FALLBACK_PRIORITIES = ['Critical', 'High', 'Medium', 'Low'];
const OFFICE_DAY = { start: '08:00', end: '17:00' };
const EMPTY = {
  name: '',
  always_open: false,
  hours: Object.fromEntries(DAYS.map(([k]) => [k, ['sat', 'sun'].includes(k) ? [] : [{ ...OFFICE_DAY }]])),
  exclude_holidays: true,
  closed_dates: [],
  targets: {}
};

const errorOf = (e, fallback) => e?.response?.data?.error || fallback;
const minutes = (t) => {
  const m = /^(\d{2}):(\d{2})$/.exec(String(t || ''));
  return m ? Number(m[1]) * 60 + Number(m[2]) : null;
};
const dayWords = (iso) => {
  const d = new Date(`${iso}T12:00:00Z`);
  return Number.isNaN(d.getTime()) ? iso
    : d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
};
const momentWords = (iso) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? ''
    : d.toLocaleString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Malta' });
};

/** A profile from the server as the form edits it: every day present, targets as strings. */
function toForm(p) {
  const hours = Object.fromEntries(DAYS.map(([k]) => [k, (p?.hours?.[k] || []).map((s) => ({ start: s.start, end: s.end }))]));
  const targets = {};
  for (const [name, t] of Object.entries(p?.targets || {})) {
    targets[name] = { response_hours: t?.response_hours ?? '', resolution_hours: t?.resolution_hours ?? '' };
  }
  return {
    name: p?.name || '',
    always_open: p?.always_open === true,
    hours,
    exclude_holidays: p ? p.exclude_holidays === true : true,
    closed_dates: (p?.closed_dates || []).map((c) => ({ date: c.date, label: c.label || '' })),
    targets
  };
}

/** What the form can say before the server does. */
function checkForm(f) {
  if (!f.name.trim()) return { field: 'name', text: 'Give the profile a name.' };
  if (f.name.trim().length > 60) return { field: 'name', text: 'Keep the name to 60 characters.' };
  if (!f.always_open) {
    let open = false;
    for (const [k, label] of DAYS) {
      const spans = f.hours[k] || [];
      for (let i = 0; i < spans.length; i += 1) {
        const a = minutes(spans[i].start);
        const b = spans[i].end === '24:00' ? 1440 : minutes(spans[i].end);
        if (a === null || b === null) return { field: 'hours', text: `${label}: give a start and an end.` };
        if (b <= a) return { field: 'hours', text: `${label}: the end must be after the start.` };
        if (i > 0 && a < (minutes(spans[i - 1].end) ?? 0)) return { field: 'hours', text: `${label}: the hours overlap.` };
        open = true;
      }
    }
    if (!open) return { field: 'hours', text: 'Give it some working hours, or make it 24/7.' };
  }
  for (const [name, t] of Object.entries(f.targets)) {
    for (const v of [t.response_hours, t.resolution_hours]) {
      if (v !== '' && v !== null && v !== undefined && !(Number(v) > 0)) {
        return { field: 'targets', text: `${name}: a target is a number of hours above 0, or blank.` };
      }
    }
  }
  return null;
}

/** The form as the server takes it. */
function toBody(f) {
  const targets = {};
  for (const [name, t] of Object.entries(f.targets)) {
    const r = t.response_hours === '' ? undefined : Number(t.response_hours);
    const s = t.resolution_hours === '' ? undefined : Number(t.resolution_hours);
    if (r === undefined && s === undefined) continue;
    targets[name] = { ...(r !== undefined ? { response_hours: r } : {}), ...(s !== undefined ? { resolution_hours: s } : {}) };
  }
  return {
    name: f.name.trim(),
    always_open: f.always_open,
    hours: f.always_open ? {} : f.hours,
    exclude_holidays: f.exclude_holidays,
    closed_dates: f.closed_dates.filter((c) => c.date).map((c) => ({ date: c.date, label: c.label.trim() })),
    targets
  };
}

function DayRow({ k, label, spans, onChange, disabled }) {
  const open = spans.length > 0;
  const set = (i, field, v) => onChange(spans.map((s, j) => (j === i ? { ...s, [field]: v } : s)));
  return (
    <div className="sp-day" data-day={k} data-open={open || undefined}>
      <label className="sp-check sp-day-name">
        <input type="checkbox" checked={open} disabled={disabled}
               onChange={(e) => onChange(e.target.checked ? [{ ...OFFICE_DAY }] : [])} />
        <span>{label}</span>
      </label>
      <div className="sp-spans">
        {!open && <span className="stn-quiet">Closed</span>}
        {spans.map((s, i) => (
          <span className="sp-span" key={i}>
            <input type="time" aria-label={`${label} from`} value={s.start} disabled={disabled} step="900"
                   onChange={(e) => set(i, 'start', e.target.value)} />
            <span aria-hidden="true">–</span>
            <input type="time" aria-label={`${label} until`} value={s.end === '24:00' ? '23:59' : s.end} disabled={disabled} step="900"
                   onChange={(e) => set(i, 'end', e.target.value === '23:59' ? '24:00' : e.target.value)} />
            {spans.length > 1 && (
              <button type="button" className="sp-x" aria-label={`Remove ${label} ${s.start}–${s.end}`} disabled={disabled}
                      onClick={() => onChange(spans.filter((_, j) => j !== i))}>×</button>
            )}
          </span>
        ))}
        {open && spans.length < 4 && (
          <button type="button" className="sp-link" disabled={disabled}
                  onClick={() => {
                    const last = spans[spans.length - 1];
                    const from = Math.min(1380, (minutes(last?.end) ?? 720) + 60);
                    const hh = (m) => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
                    onChange([...spans, { start: hh(from), end: hh(Math.min(1439, from + 60)) }]);
                  }}>
            Add hours
          </button>
        )}
      </div>
    </div>
  );
}

function ProfileForm({ initial, priorities, holidays, saving, problem, onSave, onCancel, submitLabel }) {
  const [f, setF] = useState(() => toForm(initial || EMPTY));
  const [local, setLocal] = useState(null);
  const [preview, setPreview] = useState(null);
  const [newDate, setNewDate] = useState({ date: '', label: '' });
  const nameRef = useRef(null);
  useEffect(() => { nameRef.current?.focus(); }, []);
  const set = (k, v) => { setF((x) => ({ ...x, [k]: v })); setLocal(null); };

  // "A ticket raised now with an 8-hour target would be due ..." - asked of
  // the server, which is the one that counts, a moment after the last change.
  useEffect(() => {
    if (checkForm(f)) { setPreview(null); return undefined; }
    let live = true;
    const t = setTimeout(() => {
      axios.post(`${BASE}/preview`, { profile: toBody(f), hours: 8 })
        .then((r) => { if (live) setPreview(r.data || null); })
        .catch(() => { if (live) setPreview(null); });
    }, 350);
    return () => { live = false; clearTimeout(t); };
  }, [f]);

  const copyMonday = () => set('hours', Object.fromEntries(DAYS.map(([k]) => [k,
    ['tue', 'wed', 'thu', 'fri'].includes(k) ? f.hours.mon.map((s) => ({ ...s })) : f.hours[k]])));
  const addDate = () => {
    if (!newDate.date) return;
    if (f.closed_dates.some((c) => c.date === newDate.date)) { setNewDate({ date: '', label: '' }); return; }
    set('closed_dates', [...f.closed_dates, { ...newDate }].sort((a, b) => (a.date < b.date ? -1 : 1)));
    setNewDate({ date: '', label: '' });
  };
  const target = (name, field, v) => set('targets', { ...f.targets, [name]: { response_hours: '', resolution_hours: '', ...f.targets[name], [field]: v } });

  const submit = (e) => {
    e.preventDefault();
    const bad = checkForm(f);
    if (bad) { setLocal(bad); return; }
    onSave(toBody(f));
  };
  const shown = local || problem;
  const today = new Date().toISOString().slice(0, 10);

  return (
    <form className="stn-form sp-form" onSubmit={submit} noValidate aria-label={submitLabel}>
      {shown && <div className="stn-banner is-problem" role="alert"><span>{shown.text || shown}</span></div>}
      <div className="stn-form-grid">
        <div className="stn-field stn-field-grow">
          <label htmlFor="sp-name">Name <span className="cfm-req" aria-hidden="true">*</span></label>
          <input id="sp-name" ref={nameRef} value={f.name} maxLength={60} placeholder="e.g. Alpha Ltd contract"
                 aria-invalid={shown?.field === 'name' || undefined} onChange={(e) => set('name', e.target.value)} />
        </div>
      </div>

      <fieldset className="sp-group">
        <legend>Working hours <span className="stn-quiet">· Malta time, summer and winter</span></legend>
        <label className="sp-check sp-always">
          <input type="checkbox" checked={f.always_open} onChange={(e) => set('always_open', e.target.checked)} />
          <span><strong>24/7</strong> - every hour counts, nights and weekends included</span>
        </label>
        {!f.always_open && (
          <div className="sp-week" aria-invalid={shown?.field === 'hours' || undefined}>
            {DAYS.map(([k, label]) => (
              <DayRow key={k} k={k} label={label} spans={f.hours[k] || []}
                      onChange={(spans) => set('hours', { ...f.hours, [k]: spans })} />
            ))}
            <button type="button" className="sp-link sp-copy" onClick={copyMonday} disabled={!f.hours.mon.length}>
              Copy Monday to Tuesday–Friday
            </button>
          </div>
        )}
      </fieldset>

      <fieldset className="sp-group">
        <legend>Closed days</legend>
        <label className="sp-check">
          <input type="checkbox" checked={f.exclude_holidays} onChange={(e) => set('exclude_holidays', e.target.checked)} />
          <span>Malta&rsquo;s public holidays are closed days</span>
        </label>
        {f.exclude_holidays && holidays.length > 0 && (
          <details className="sp-holidays">
            <summary>The next {holidays.length} (Good Friday worked out each year)</summary>
            <ul>{holidays.map((h) => <li key={h.date}><span>{dayWords(h.date)}</span> {h.name}</li>)}</ul>
          </details>
        )}
        <p className="stn-quiet sp-sub">And these days as well - an office closure, a staff day:</p>
        {f.closed_dates.length > 0 && (
          <ul className="sp-dates" aria-label="Extra closed days">
            {f.closed_dates.map((c) => (
              <li key={c.date}>
                <span className="sp-date">{dayWords(c.date)}</span>
                {c.label && <span className="stn-quiet">{c.label}</span>}
                <button type="button" className="sp-x" aria-label={`Remove ${c.date}`}
                        onClick={() => set('closed_dates', f.closed_dates.filter((x) => x.date !== c.date))}>×</button>
              </li>
            ))}
          </ul>
        )}
        <div className="sp-add-date">
          <input type="date" aria-label="Closed day" min={today} value={newDate.date}
                 onChange={(e) => setNewDate((x) => ({ ...x, date: e.target.value }))} />
          <input type="text" aria-label="What it is (optional)" placeholder="What it is (optional)" maxLength={60} value={newDate.label}
                 onChange={(e) => setNewDate((x) => ({ ...x, label: e.target.value }))}
                 onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addDate(); } }} />
          <button type="button" className="btn btn-small btn-secondary" onClick={addDate} disabled={!newDate.date}>Add day</button>
        </div>
      </fieldset>

      <fieldset className="sp-group">
        <legend>Targets <span className="stn-quiet">· optional, in working hours</span></legend>
        <p className="stn-quiet sp-sub">
          Leave blank to use <Link to="/settings/tickets/sla">SLA rules</Link> for the ticket&rsquo;s category. A figure here
          wins for every ticket on this profile - a client&rsquo;s contract, say.
        </p>
        <div className="stn-table-wrap">
          <table className="stn-table sp-targets" aria-invalid={shown?.field === 'targets' || undefined}>
            <thead><tr><th scope="col">Priority</th><th scope="col">Respond within</th><th scope="col">Resolve within</th></tr></thead>
            <tbody>
              {priorities.map((name) => (
                <tr key={name}>
                  <th scope="row">{name}</th>
                  {['response_hours', 'resolution_hours'].map((field) => (
                    <td key={field}>
                      <input type="number" min="0.25" step="0.25" inputMode="decimal" placeholder="From SLA rules"
                             aria-label={`${name} ${field === 'response_hours' ? 'response' : 'resolution'} in working hours`}
                             value={f.targets[name]?.[field] ?? ''} onChange={(e) => target(name, field, e.target.value)} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </fieldset>

      <p className="sp-preview" role="status" aria-live="polite">
        {preview?.due
          ? <>A ticket raised now with an 8-hour target would be due <strong>{momentWords(preview.due)}</strong>.</>
          : preview ? 'With these hours a deadline would never come.' : ''}
      </p>
      <div className="stn-actions">
        <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving…' : submitLabel}</button>
        <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={saving}>Cancel</button>
      </div>
    </form>
  );
}

export default function SLAProfiles() {
  const [data, setData] = useState(null);
  const [priorities, setPriorities] = useState(FALLBACK_PRIORITIES);
  const [problem, setProblem] = useState(null);
  const [formProblem, setFormProblem] = useState(null);
  const [done, setDone] = useState(null);
  const [editing, setEditing] = useState(null);     // null | 'new' | profile
  const [saving, setSaving] = useState(false);
  const [asking, setAsking] = useState(null);       // a profile id being deleted
  const [rule, setRule] = useState({ category_id: '', company_id: '', profile_id: '' });
  const [reapply, setReapply] = useState('idle');   // idle | asking | busy

  const load = useCallback(async () => {
    try {
      const r = await axios.get(BASE);
      const d = r.data && typeof r.data === 'object' && !Array.isArray(r.data) ? r.data : { ready: true };
      setData({ ready: d.ready !== false, error: d.error || null, profiles: d.profiles || [], rules: d.rules || [],
        categories: d.categories || [], companies: d.companies || [], companies_ready: d.companies_ready !== false,
        holidays: d.holidays || [] });
    } catch (e) {
      setProblem(errorOf(e, 'Could not load the SLA working hours.'));
      setData((x) => x || { ready: true, profiles: [], rules: [], categories: [], companies: [], holidays: [] });
    }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    axios.get(`${API_BASE}/priorities`).then((r) => {
      const names = (Array.isArray(r.data) ? r.data : [])
        .sort((a, b) => (Number(b.level) || 0) - (Number(a.level) || 0)).map((p) => p.name).filter(Boolean);
      if (names.length) setPriorities(names);
    }).catch(() => {});
  }, []);
  const flash = (text) => { setDone(text); setTimeout(() => setDone((x) => (x === text ? null : x)), 4000); };

  const profiles = useMemo(() => data?.profiles || [], [data]);
  const byId = useMemo(() => new Map(profiles.map((p) => [String(p.id), p])), [profiles]);
  const outdated = profiles.reduce((n, p) => n + (p.open_outdated || 0), 0);

  const save = async (body) => {
    setSaving(true);
    setFormProblem(null);
    try {
      if (editing === 'new') await axios.post(BASE, body);
      else await axios.put(`${BASE}/${editing.id}`, body);
      flash(`${body.name} ${editing === 'new' ? 'added' : 'saved'}.`);
      setEditing(null);
      await load();
    } catch (e) {
      setFormProblem({ text: errorOf(e, 'Could not save that profile.'), field: e?.response?.data?.field });
    } finally { setSaving(false); }
  };

  const act = async (run, okText, failText) => {
    setProblem(null);
    try { await run(); if (okText) flash(okText); await load(); } catch (e) { setProblem(errorOf(e, failText)); }
  };

  const addRule = async (e) => {
    e.preventDefault();
    if (!rule.profile_id || (!rule.category_id && !rule.company_id)) return;
    await act(() => axios.post(`${BASE}/rules`, {
      profile_id: rule.profile_id, category_id: rule.category_id || null, company_id: rule.company_id || null
    }), 'Rule added. New tickets it matches get that profile.', 'Could not add that rule.');
    setRule({ category_id: '', company_id: '', profile_id: '' });
  };

  const runReapply = async () => {
    setReapply('busy');
    setProblem(null);
    try {
      const r = await axios.post(`${BASE}/reapply`, {});
      flash(r.data?.changed
        ? `Moved ${r.data.changed} of ${r.data.checked} open ticket${r.data.checked === 1 ? '' : 's'} onto their profile as it is now.`
        : `All ${r.data?.checked ?? 0} open tickets were already up to date.`);
      await load();
    } catch (e) {
      setProblem(errorOf(e, 'Could not re-apply the profiles.'));
    } finally { setReapply('idle'); }
  };

  if (!data) return <p className="stn-quiet">Loading…</p>;
  const ready = data.ready;
  const ruleWhen = (r) => [r.category_name && `${r.category_name} tickets`, r.company_name && `${r.category_name ? 'at' : 'Tickets for'} ${r.company_name}`]
    .filter(Boolean).join(' ');

  return (
    <div className="sp">
      {problem && (
        <div className="stn-banner is-problem" role="alert">
          <span>{problem}</span>
          <button type="button" className="btn btn-small btn-secondary" onClick={() => setProblem(null)}>Dismiss</button>
        </div>
      )}
      {!ready && (
        <div className="stn-banner is-warn" role="status">
          <span>
            Working hours are not set up on this database yet - every SLA still runs on the plain clock.
            Run <code>npm run migrate:114</code> (or <code>npm run migrate</code>) on the server.
          </span>
        </div>
      )}
      <p className="stn-flash" role="status" aria-live="polite">{done || ''}</p>

      <section className="stn-card" id="s-profiles" tabIndex={-1} aria-labelledby="sp-profiles-h">
        <div className="sp-head">
          <h3 className="stn-card-title" id="sp-profiles-h">Profiles</h3>
          {ready && editing === null && (
            <button type="button" className="btn btn-small btn-primary" onClick={() => { setFormProblem(null); setEditing('new'); }}>
              New profile
            </button>
          )}
        </div>
        <p className="stn-help">
          An SLA&rsquo;s hours are working hours on the ticket&rsquo;s profile. A ticket keeps the profile it was raised
          with, so changing one here does not move a deadline already promised.
        </p>
        {editing === 'new' && (
          <ProfileForm initial={null} priorities={priorities} holidays={data.holidays} saving={saving} problem={formProblem}
                       onSave={save} onCancel={() => setEditing(null)} submitLabel="Add profile" />
        )}
        <ul className="stn-grid stn-grid-wide sp-list" aria-label="SLA profiles">
          {ready && profiles.length === 0 && <li className="stn-quiet">No profiles yet.</li>}
          {profiles.map((p) => (editing && editing !== 'new' && editing.id === p.id ? (
            <li key={p.id} className="stn-item sp-item is-editing">
              <h4 className="stn-item-name">Edit {p.name}</h4>
              <ProfileForm initial={p} priorities={priorities} holidays={data.holidays} saving={saving} problem={formProblem}
                           onSave={save} onCancel={() => setEditing(null)} submitLabel="Save changes" />
            </li>
          ) : (
            <li key={p.id} className="stn-item sp-item" data-profile={p.name}>
              <div className="stn-item-head">
                <h4 className="stn-item-name">{p.name}</h4>
                {p.is_default && <span className="stn-pill is-good">Default</span>}
              </div>
              <p className="stn-item-desc">{p.summary}</p>
              {Object.keys(p.targets || {}).length > 0 && (
                <p className="sp-meta">
                  Own targets: {Object.entries(p.targets).map(([n, t]) => `${n} ${[t.response_hours && `${t.response_hours}h response`, t.resolution_hours && `${t.resolution_hours}h fix`].filter(Boolean).join(', ')}`).join(' · ')}
                </p>
              )}
              <p className="sp-meta">
                {p.is_default ? 'Tickets no rule names' : (p.rules || []).length ? (p.rules || []).map(ruleWhen).join('; ') : 'No rule uses it yet'}
                {' · '}{p.open_tickets} open ticket{p.open_tickets === 1 ? '' : 's'}
              </p>
              {p.open_outdated > 0 && (
                <p className="sp-meta sp-outdated">
                  {p.open_outdated} of them still on its hours from before the last change.
                </p>
              )}
              {asking === p.id ? (
                <div className="stn-confirm" role="group" aria-label={`Delete ${p.name}?`}>
                  <p>Delete {p.name}? Its rules go with it. Tickets already on it keep their deadlines.</p>
                  <div className="stn-confirm-actions">
                    <button type="button" className="btn btn-small btn-danger"
                            onClick={() => { setAsking(null); act(() => axios.delete(`${BASE}/${p.id}`), `${p.name} deleted.`, 'Could not delete that profile.'); }}>
                      Delete
                    </button>
                    <button type="button" className="btn btn-small btn-secondary" onClick={() => setAsking(null)}>Keep it</button>
                  </div>
                </div>
              ) : (
                <div className="stn-row-actions">
                  <button type="button" className="btn btn-small btn-secondary" aria-label={`Edit ${p.name}`}
                          disabled={editing !== null} onClick={() => { setFormProblem(null); setEditing(p); }}>Edit</button>
                  {!p.is_default && (
                    <button type="button" className="btn btn-small btn-secondary" aria-label={`Make ${p.name} the default`}
                            onClick={() => act(() => axios.post(`${BASE}/${p.id}/default`, {}), `${p.name} is now the default.`, 'Could not change the default.')}>
                      Make default
                    </button>
                  )}
                  {!p.is_default && (
                    <button type="button" className="btn btn-small btn-danger-outline" aria-label={`Delete ${p.name}`}
                            onClick={() => setAsking(p.id)}>Delete</button>
                  )}
                </div>
              )}
            </li>
          )))}
        </ul>
      </section>

      <section className="stn-card" id="s-rules" tabIndex={-1} aria-labelledby="sp-rules-h">
        <h3 className="stn-card-title" id="sp-rules-h">Which tickets get which profile</h3>
        <p className="stn-help">
          A rule names a category, a client company, or both. The most specific one wins - category and company,
          then company, then category - and anything else gets the default. A ticket&rsquo;s company is the company
          of the device it is about.
        </p>
        {(data.rules || []).length > 0 ? (
          <div className="stn-table-wrap">
            <table className="stn-table sp-rules">
              <thead><tr><th scope="col">When a ticket is</th><th scope="col">it runs on</th><th scope="col"><span className="stn-sr">Remove</span></th></tr></thead>
              <tbody>
                {data.rules.map((r) => (
                  <tr key={r.id} data-rule={r.id}>
                    <td>{[r.category_name ? `in ${r.category_name}` : null, r.company_name ? `for ${r.company_name}` : null].filter(Boolean).join(', ')}</td>
                    <td>{byId.get(String(r.profile_id))?.name || '—'}</td>
                    <td className="sp-cell-end">
                      <button type="button" className="btn btn-small btn-danger-outline"
                              aria-label={`Remove the rule for ${[r.category_name, r.company_name].filter(Boolean).join(' at ')}`}
                              onClick={() => act(() => axios.delete(`${BASE}/rules/${r.id}`), 'Rule removed.', 'Could not remove that rule.')}>
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="stn-quiet">{ready ? 'No rules - every ticket gets the default.' : 'Rules come with migration 114.'}</p>
        )}
        {ready && profiles.length > 0 && (
          <form className="sp-rule-form" onSubmit={addRule} aria-label="Add a rule">
            <div className="stn-field">
              <label htmlFor="sp-rule-cat">Category</label>
              <select id="sp-rule-cat" value={rule.category_id} onChange={(e) => setRule((x) => ({ ...x, category_id: e.target.value }))}>
                <option value="">Any category</option>
                {data.categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div className="stn-field">
              <label htmlFor="sp-rule-co">Company</label>
              <select id="sp-rule-co" value={rule.company_id} disabled={!data.companies_ready || !data.companies.length}
                      onChange={(e) => setRule((x) => ({ ...x, company_id: e.target.value }))}>
                <option value="">{data.companies.length ? 'Any company' : 'No companies in the register'}</option>
                {data.companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div className="stn-field">
              <label htmlFor="sp-rule-profile">Profile</label>
              <select id="sp-rule-profile" value={rule.profile_id} onChange={(e) => setRule((x) => ({ ...x, profile_id: e.target.value }))}>
                <option value="">Choose…</option>
                {profiles.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <button type="submit" className="btn btn-primary sp-rule-add"
                    disabled={!rule.profile_id || (!rule.category_id && !rule.company_id)}>Add rule</button>
          </form>
        )}
      </section>

      <section className="stn-card" id="s-reapply" tabIndex={-1} aria-labelledby="sp-reapply-h">
        <h3 className="stn-card-title" id="sp-reapply-h">Open tickets</h3>
        <p className="stn-help">
          New tickets always get the profile their rules give them today. Open tickets keep the one they were raised
          with until you re-apply - after changing a profile&rsquo;s hours or a rule, or to move tickets raised before
          working hours were introduced (they were kept on 24/7, the clock they had always run on).
        </p>
        {outdated > 0 && <p className="sp-meta sp-outdated">{outdated} open ticket{outdated === 1 ? ' is' : 's are'} on hours from before a later change.</p>}
        {reapply === 'asking' ? (
          <div className="stn-confirm" role="group" aria-label="Re-apply to open tickets?">
            <p>
              Every open ticket gets the profile its category and device give it now, with that profile&rsquo;s hours as
              they are now. Their deadlines move accordingly; breaches already announced stay announced.
            </p>
            <div className="stn-confirm-actions">
              <button type="button" className="btn btn-small btn-primary" onClick={runReapply}>Re-apply</button>
              <button type="button" className="btn btn-small btn-secondary" onClick={() => setReapply('idle')}>Cancel</button>
            </div>
          </div>
        ) : (
          <div className="stn-actions">
            <button type="button" className="btn btn-secondary" disabled={!ready || reapply === 'busy'} onClick={() => setReapply('asking')}>
              {reapply === 'busy' ? 'Re-applying…' : 'Re-apply to open tickets'}
            </button>
          </div>
        )}
      </section>
    </div>
  );
}
